import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { PartyPopper } from 'lucide-react';
import { useAuth } from 'context/auth/Auth';
import paths from 'routes/paths';
import breakpoints from 'theme/breakpoints';
import useCountdown from './useCountdown';
import {
    Eyebrow,
    CountdownChip,
    PrimaryButton,
    GhostButton,
    campaign,
} from './shared';

const ASSETS = '/assets/images/promo/3-anos';

export default function HeroSection() {
    const navigate = useNavigate();
    const { isAuth } = useAuth();
    const daysLeft = useCountdown();

    function goCreateEvent() {
        navigate(isAuth ? paths.dashboard.events : paths.partner.createAccount);
    }

    return (
        <Wrapper>
            <BgBlobRight src={`${ASSETS}/blob-w.png`} alt="" aria-hidden="true" />

            <BalloonCat
                $pos="left"
                src={`${ASSETS}/balao-branco-gato.png`}
                alt=""
                aria-hidden="true"
            />
            <BalloonCat
                $pos="right"
                src={`${ASSETS}/balao-rosa-gato.png`}
                alt=""
                aria-hidden="true"
            />

            <Content>
                <TextCol>
                    <Eyebrow $onDark>
                        <PartyPopper size={14} />
                        #3anosdewhizr · 1 set a 30 nov
                    </Eyebrow>

                    <Headline>
                        Produzir eventos já tem <Muted>taxa emocional</Muted> suficiente.
                        <br />
                        Então a gente diminuiu a <Highlight>financeira</Highlight>.
                    </Headline>

                    <Subtext>
                        Bora comemorar 3 anos de rolê? A gente cortou as taxas de venda por
                        3 meses — vale pra quem já tá na correria com a gente e pra quem tá
                        chegando agora.
                    </Subtext>

                    <CtaRow>
                        <PrimaryButton onClick={goCreateEvent}>
                            Quero criar meu evento
                        </PrimaryButton>
                        <GhostButton $onDark onClick={() => document.getElementById('taxas-3-anos')?.scrollIntoView({ behavior: 'smooth' })}>
                            Ver as condições
                        </GhostButton>
                    </CtaRow>

                    {daysLeft > 0 && (
                        <CountdownWrap>
                            <CountdownChip $onDark>
                                Faltam <strong>{daysLeft}</strong> {daysLeft === 1 ? 'dia' : 'dias'} pra garantir sua condição
                            </CountdownChip>
                        </CountdownWrap>
                    )}
                </TextCol>

                <ImageCol>
                    <CakeWrap>
                        <CakeImg src={`${ASSETS}/cake-3-anos.png`} alt="Bolo de aniversário de 3 anos da Whizr" />
                        <FeeBadge $pos="top">6% <span>no Pix</span></FeeBadge>
                        <FeeBadge $pos="bottom">8% <span>no cartão</span></FeeBadge>
                    </CakeWrap>
                </ImageCol>
            </Content>
        </Wrapper>
    );
}

const Wrapper = styled.section`
    position: relative;
    overflow: hidden;
    background: linear-gradient(135deg, ${campaign.purple} 0%, ${campaign.purpleDeep} 100%);
    padding: 120px 20px 64px;

    @media screen and (min-width: ${breakpoints.smd}) {
        padding: 140px 32px 88px;
    }
`;

const BgBlobRight = styled.img`
    position: absolute;
    right: -40px;
    bottom: -30px;
    width: 220px;
    opacity: 0.4;
    pointer-events: none;
    user-select: none;
    display: none;

    @media screen and (min-width: ${breakpoints.smd}) {
        display: block;
        width: 320px;
    }
`;

const BalloonCat = styled.img`
    position: absolute;
    pointer-events: none;
    user-select: none;
    display: none;

    @media screen and (min-width: ${breakpoints.lg}) {
        display: block;
        width: 120px;
        top: ${({ $pos }) => $pos === 'left' ? '110px' : '60px'};
        left: ${({ $pos }) => $pos === 'left' ? '4%' : 'auto'};
        right: ${({ $pos }) => $pos === 'right' ? '4%' : 'auto'};
    }
`;

const Content = styled.div`
    position: relative;
    z-index: 1;
    max-width: 1080px;
    margin: 0 auto;
    display: flex;
    flex-direction: column-reverse;
    align-items: center;
    gap: 32px;

    @media screen and (min-width: ${breakpoints.lg}) {
        flex-direction: row;
        align-items: center;
        gap: 24px;
    }
`;

const TextCol = styled.div`
    flex: 1.1;
    text-align: center;

    @media screen and (min-width: ${breakpoints.lg}) {
        text-align: left;
    }
`;

const ImageCol = styled.div`
    flex: 1;
    display: flex;
    justify-content: center;
`;

const CakeWrap = styled.div`
    position: relative;
    width: 100%;
    max-width: 380px;

    @media screen and (min-width: ${breakpoints.lg}) {
        max-width: 460px;
    }
`;

const CakeImg = styled.img`
    width: 100%;
    display: block;
`;

const FeeBadge = styled.div`
    position: absolute;
    display: flex;
    align-items: baseline;
    gap: 4px;
    padding: 7px 14px;
    border-radius: 50px;
    background: rgba(255,255,255,0.95);
    color: ${campaign.purple};
    font-size: 20px;
    font-weight: 800;
    box-shadow: 0 6px 18px rgba(0,0,0,0.18);
    white-space: nowrap;

    span {
        font-size: 12px;
        font-weight: 600;
        color: ${campaign.purpleDeep};
    }

    ${({ $pos }) => $pos === 'top' && `
        top: 6%;
        right: 2%;
    `}

    ${({ $pos }) => $pos === 'bottom' && `
        bottom: 10%;
        left: -4%;
    `}
`;

const Headline = styled.h1`
    font-size: 28px;
    line-height: 1.25;
    font-weight: 800;
    color: #fff;
    margin: 0 0 18px;
    letter-spacing: -0.01em;

    @media screen and (min-width: ${breakpoints.smd}) {
        font-size: 38px;
    }

    @media screen and (min-width: ${breakpoints.lg}) {
        font-size: 44px;
    }
`;

const Muted = styled.span`
    color: rgba(255,255,255,0.6);
`;

const Highlight = styled.span`
    color: ${campaign.gold};
`;

const Subtext = styled.p`
    font-size: 16px;
    line-height: 1.6;
    color: rgba(255,255,255,0.85);
    margin: 0 0 28px;
    max-width: 520px;

    @media screen and (min-width: ${breakpoints.lg}) {
        margin-left: 0;
        margin-right: 0;
    }

    margin-left: auto;
    margin-right: auto;

    @media screen and (min-width: ${breakpoints.smd}) {
        font-size: 17px;
    }
`;

const CtaRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
    justify-content: center;

    @media screen and (min-width: ${breakpoints.lg}) {
        justify-content: flex-start;
    }
`;

const CountdownWrap = styled.div`
    margin-top: 22px;
`;
